import axios from 'axios';
import { AppConfig, JWT } from '../config';
import { ServiceModalConstant } from './constants/ServiceModalConstant';
import { ServiceConstant } from './constants/ServiceConstant';
import { AlertAction } from './AlertAction';
import { Translate as t } from '../config/Translate';
import { isAdminPathname } from '../lib/isAdminPathname';

const GetPendingAlerts = () => async (dispatch) => {
    dispatch({ type: ServiceModalConstant.LOADING });
    try {
        const data = { type: 'getPendingAlerts' };
        const ret = await axios.post(AppConfig.API_SERVER_PATH + '/servicemodalert', data, JWT());
        if (ret.data.status == 'success') {
            dispatch({ type: ServiceModalConstant.GET_PENDING_ALERTS_SUCCESS, payload: ret.data.data });
        } else {
            dispatch({ type: ServiceModalConstant.GET_PENDING_ALERTS_FAIL });
        }
    } catch (error) {
        dispatch({ type: ServiceModalConstant.GET_PENDING_ALERTS_FAIL });
    }
    dispatch({ type: ServiceModalConstant.LOADED });
};

const ProcessAlert = (alertId, serviceId, priority, assignee, ticketKey) => async (dispatch) => {
    try {
        const data = {
            type: 'processAlert',
            data: { alertId, serviceId, priority, assignee }
        };
        const ret = await axios.post(AppConfig.API_SERVER_PATH + '/servicemodalert', data, JWT());
        if (ret.data.status == 'success') {
            dispatch({ type: ServiceModalConstant.PROCESS_ALERT_SUCCESS, payload: ret.data.data });
            // 부모 페이지에서 같은 티켓을 보고 있을 수 있으므로 서비스 데이터 새로고침
            if (ticketKey) {
                try {
                    const svcRet = await axios.get(AppConfig.API_SERVER_PATH + '/service/getbyticket/' + ticketKey, JWT());
                    if (svcRet.data.status == 'success') {
                        dispatch({ type: ServiceConstant.SERVICE_GET_ONE, service: svcRet.data.data });
                    }
                } catch (e) { /* 부모 페이지 새로고침 실패는 핵심 기능 아님 — silent */ }
            }
        } else {
            dispatch({ type: ServiceModalConstant.PROCESS_ALERT_FAIL });
            dispatch(AlertAction.Error(ret.data));
        }
    } catch (error) {
        dispatch({ type: ServiceModalConstant.PROCESS_ALERT_FAIL });
        dispatch(AlertAction.Error(error));
    }
};

const GetAssigneeAvailability = (companyId) => async (dispatch) => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const data = { type: 'getAssigneeAvailability', data: { companyId, timezone } };
        const ret = await axios.post(AppConfig.API_SERVER_PATH + '/servicemodalert', data, JWT());
        if (ret.data.status == 'success') {
            dispatch({ type: ServiceModalConstant.GET_ASSIGNEE_AVAILABILITY_SUCCESS, payload: ret.data.data });
        }
    } catch (error) {
        // silent fail
    }
};

// IMP-282: InputCombo getCombo 용 — 가용성 포함 assignee 후보를 raw users 배열로 반환(dispatch 아님)
const GetAssigneeAvailabilityList = async (companyId) => {
    try {
        if (!companyId) return [];
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const data = { type: 'getAssigneeAvailability', data: { companyId, timezone } };
        const ret = await axios.post(AppConfig.API_SERVER_PATH + '/servicemodalert', data, JWT());
        return (ret.data.status === 'success' && Array.isArray(ret.data.data?.users)) ? ret.data.data.users : [];
    } catch (e) {
        console.warn('[GetAssigneeAvailabilityList] 가용성 조회 실패', { companyId, error: e?.message });
        return [];
    }
};

const GetIssueManagers = (companyId) => async (dispatch) => {
    try {
        const data = { type: 'getIssueManagers', data: { companyId } };
        const ret = await axios.post(AppConfig.API_SERVER_PATH + '/company', data, JWT());
        if (ret.data.status == 'success') {
            dispatch({ type: ServiceModalConstant.GET_ISSUE_MANAGERS_SUCCESS, payload: ret.data.data });
        } else {
            dispatch({ type: ServiceModalConstant.GET_ISSUE_MANAGERS_FAIL });
        }
    } catch (error) {
        dispatch({ type: ServiceModalConstant.GET_ISSUE_MANAGERS_FAIL });
    }
};

const SetIssueManagers = (companyId, issueManagerIds) => async (dispatch) => {
    try {
        const data = { type: 'setIssueManagers', data: { companyId, issueManagerIds } };
        const ret = await axios.post(AppConfig.API_SERVER_PATH + '/company', data, JWT());
        if (ret.data.status == 'success') {
            dispatch(AlertAction.Success(t('service.issueManager.appliedSuccess')));
            dispatch({ type: ServiceModalConstant.SET_ISSUE_MANAGERS_SUCCESS, payload: ret.data.data });
        } else {
            dispatch(AlertAction.Error(ret.data));
        }
    } catch (error) {
        dispatch(AlertAction.Error(error));
    }
};

/**
 * BUG-077: WS alert 수신 시 백엔드에서 최신 pending alerts를 조회한 뒤,
 * alertId 집합 비교로 신규 alert를 정확히 식별하여 모달 표시.
 * 삭제된 티켓의 alert는 백엔드에서 isDel 필터링되므로 race condition 해소.
 */
const RefreshAndShowIfNew = () => async (dispatch, getState) => {
    const isAdminPath = () => isAdminPathname(window.location.pathname);
    if (!isAdminPath()) return;

    const prevAlerts = getState().ServiceModalReducer?.pendingAlerts || [];
    const prevIds = new Set(prevAlerts.map(a => a._id));
    try {
        const data = { type: 'getPendingAlerts' };
        const ret = await axios.post(AppConfig.API_SERVER_PATH + '/servicemodalert', data, JWT());
        // TOCTOU: await 후 Admin 경로 이탈 시 dispatch 차단
        if (!isAdminPath()) return;

        const payload = ret.data?.data ?? { alerts: [], totalCount: 0 };
        if (ret.data?.status === 'success') {
            const alerts = payload.alerts ?? [];
            dispatch({ type: ServiceModalConstant.GET_PENDING_ALERTS_SUCCESS, payload });
            // alertId 집합 비교로 신규 alert 식별 (count 비교의 동시 삭제+추가 문제 해소)
            const newAlerts = alerts.filter(a => !prevIds.has(a._id));
            if (newAlerts.length > 0) {
                const latestAlert = newAlerts.reduce(
                    (latest, a) => (!latest || (a.createDate || 0) > (latest.createDate || 0) ? a : latest),
                    null
                );
                dispatch({ type: ServiceModalConstant.SHOW_MODAL, payload: { modalType: 'ISSUE_ALERT', alertData: latestAlert } });
            }
        }
    } catch (e) {
        console.warn('[RefreshAndShowIfNew] failed', { status: e?.response?.status, msg: e?.message });
    }
};

const ShowModal = (modalType, alertData) => (dispatch) => {
    dispatch({ type: ServiceModalConstant.SHOW_MODAL, payload: { modalType, alertData } });
};

const HideModal = () => (dispatch) => {
    dispatch({ type: ServiceModalConstant.HIDE_MODAL });
};

const NavigatePrev = () => ({ type: ServiceModalConstant.NAVIGATE_PREV });
const NavigateNext = () => ({ type: ServiceModalConstant.NAVIGATE_NEXT });

export const ServiceModalAction = {
    GetPendingAlerts,
    ProcessAlert,
    GetAssigneeAvailability,
    GetAssigneeAvailabilityList,
    GetIssueManagers,
    SetIssueManagers,
    RefreshAndShowIfNew,
    ShowModal,
    HideModal,
    NavigatePrev,
    NavigateNext,
};
